import React, { useState, useCallback, useEffect } from "react";
import {
  Typography,
  AddCta,
  TextField,
  AnchorMenu,
  Table,
  DateFilterModal,
  TableFooter,
  Tag,
  Loader,
} from "components";
import debounce from "lodash.debounce";
import { FaExchangeAlt } from "react-icons/fa";
import { ROUTES } from "routes/routePaths";
import {
  isObjectsEqual,
  formatUrlQuery,
  formatDateTime,
  hasPermission,
  formatFilterPayload,
} from "utils/helper";
import { useSelector } from "react-redux";
import makeRequest from "services/makeRequest";
import { permissions } from "utils/permissions";
import { routes } from "routes/apiRoutes";
import { useNavigate } from "react-router-dom";
import { BiCalendar } from "react-icons/bi";

const SecondaryRoles = () => {
  const navigate = useNavigate();
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);

  const user = useSelector((state) => state.user);
  const userAuthorities = user?.user?.authorities;

  const columns = [
    {
      Header: "S/N",
      Cell: (row) => {
        let sn = Number(row.row.id) + 1;
        return sn.toString().length === 1 ? "0" + sn : sn;
      },
    },
    {
      Header: "Role Name",
      accessor: "name",
      cell: (props) => (
        <Typography state="primary" variant="body1">
          {props.value}
        </Typography>
      ),
    },
    {
      Header: "Description",
      accessor: "description",
    },
    {
      Header: "Date Created",
      accessor: "createdDate",
      Cell: (props) => formatDateTime(props.value),
    },
    {
      Header: "Last Modified",
      accessor: "lastModifiedDate",
      Cell: (props) => (props.value ? formatDateTime(props.value) : "Nil"),
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => {
        let text;
        let color;
        let bgColor;
        switch (parseInt(value)) {
          case 1:
            text = "Active";
            color = "#27AE60";
            bgColor = "#EAFCF2";
            break;
          case 2:
            text = "Pending-Creation";
            color = "#EFB008";
            bgColor = "#FCF6E6";
            break;
          default:
            text = "Disabled";
            color = "#AAA6A6";
            bgColor = "#F1F4F9";
            break;
        }

        return <Tag text={text} backgroundColor={bgColor} textColor={color} />;
      },
    },
    {
      Header: "Action",
      Cell: (props) => (
        <AnchorMenu anchorRender={() => anchorRender(props.row.original)} />
      ),
    },
  ];

  const initialFilterState = {
    orderBy: "desc",
    number: 1,
    category: "ADMIN",
    predefined: 2,
    startDate: "",
    endDate: "",
    status: "",
    name: "",
    size: 10,
  };

  const [filterState, setFilterState] = useState(initialFilterState);

  const { name, size, number, orderBy, startDate, endDate, category } =
    filterState;

  const [data, setData] = useState({
    roles: null,
    loading: false,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilterState({ ...filterState, [name]: value });
  };

  const getRolesData = (queryParams) =>
    makeRequest("get", routes.fetchRoles(queryParams));

  const resetFilter = () => {
    setFilterState(initialFilterState);
    loadRoles();
  };

  const loadRoles = async (
    queryParams = formatUrlQuery(formatFilterPayload(filterState))
  ) => {
    setData({ ...data, loading: true });
    try {
      const res = await getRolesData(queryParams);
      setData((prev) => ({
        ...prev,
        roles: res.data.data,
        loading: false,
      }));
    } catch (err) {
      console.log(err);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadWithDebounce = useCallback(
    debounce((name) => {
      if (name === "") {
        loadRoles();
      } else {
        loadRoles(formatUrlQuery({ category: 2, name }));
      }
    }, 1000),
    []
  );

  const handleSearch = (e) => {
    let val = e.target.value;
    setFilterState({ ...filterState, name: val });
    loadWithDebounce(val);
  };

  useEffect(() => {
    loadRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [number, size, orderBy, category]);

  const anchorRender = (value) => {
    return (
      <ul className="py-1" style={{ paddingLeft: "1rem" }}>
        <li
          onClick={() =>
            navigate(ROUTES.createRole, {
              state: { currentRole: value },
            })
          }
          className="cursor-pointer"
        >
          <Typography state="primary" variant="body3">
            View/Edit Role
          </Typography>
        </li>
      </ul>
    );
  };

  return (
    <div className="secondary-roles-container">
      {hasPermission(permissions.canCreateRole, userAuthorities) && (
        <AddCta
          className="sm-margin"
          text="Create a New Role"
          onClick={() => navigate(ROUTES.createRole)}
        />
      )}

      <div className="d-flex justify-content-between mt-4">
        <div
          className={`secondary-roles-header ${
            category === "AGENT" ? "secondary-roles-header--active" : ""
          }`}
          onClick={() => setFilterState({ ...filterState, category: "AGENT" })}
        >
          <Typography state="primary" variant="subtitle3">
            Agent
          </Typography>
        </div>
        <div
          className={`secondary-roles-header ${
            category === "AGGREGATOR" ? "secondary-roles-header--active" : ""
          }`}
          onClick={() =>
            setFilterState({ ...filterState, category: "AGGREGATOR" })
          }
        >
          <Typography state="primary" variant="subtitle3">
            Aggregator
          </Typography>
        </div>

        <div
          className={`secondary-roles-header ${
            category === "ADMIN" ? "secondary-roles-header--active" : ""
          }`}
          onClick={() => setFilterState({ ...filterState, category: "ADMIN" })}
        >
          <Typography state="primary" variant="subtitle3">
            Admin
          </Typography>
        </div>

        {!isObjectsEqual(initialFilterState, filterState) && (
          <div className="cursor-pointer d-none" onClick={resetFilter}>
            <Typography state="grey" variant="body3" component="span">
              Reset Filter
            </Typography>{" "}
            <FaExchangeAlt
              style={{
                color: "grey",
                fontSize: "14px",
                marginLeft: ".3rem",
              }}
            />
          </div>
        )}
      </div>

      <div className="d-md-flex justify-content-between align-items-center mt-4">
        <div className="d-md-flex align-items-center">
          <div className="d-flex align-items-center role-filters px-3">
            <Typography state="grey" variant="body1">
              Sort by:
            </Typography>
            <select
              name="sort"
              value={orderBy}
              className="table-header-select role-order"
              onChange={(e) =>
                setFilterState({
                  ...filterState,
                  orderBy: e.target.value,
                })
              }
            >
              <option value={"asc"}>Ascending Order</option>
              <option value={"desc"}>Descending Order</option>
            </select>
            <div
              className="cursor-pointer"
              onClick={() => setIsDateModalOpen(true)}
            >
              <Typography component={"span"}>Date Created</Typography>{" "}
              <BiCalendar fontSize="20px" style={{ marginTop: "-.2rem" }} />
            </div>
          </div>
          <TextField
            name="name"
            id="name"
            value={name}
            onChange={handleSearch}
            placeholder={"Search by Role Name"}
            size="sm"
            required={false}
            searchField
            className="tiny-bordered-textfield"
            containerClassName={"sm-margin d-none"}
          />
        </div>

        {/* <AddCta
          className="sm-margin"
          text="Create a New Role"
          onClick={() => navigate(ROUTES.createRole)}
        /> */}
      </div>
      <div>
        <div className="mb-2 d-flex align-items-center">
          <Typography variant="body0" className="mt-4" state="primary">
            Total Roles({data?.roles?.totalElements || 0})
          </Typography>{" "}
          &nbsp; &nbsp;
          {data.loading && (
            <span className="mt-4">
              <Loader height={20} width={2} />
            </span>
          )}
        </div>

        <Table
          //   centerData
          columns={columns}
          data={data?.roles?.content || []}
          loading={data.loading}
        />
        {data?.roles?.content?.length ? (
          <div style={{ margin: "4rem 0" }}>
            <TableFooter
              pageSize={size}
              handleChange={handleChange}
              availableDataLength={data?.roles?.content?.length}
              totalDataLength={data?.roles?.totalElements}
              handlePageClick={(pageNo) =>
                setFilterState({ ...filterState, number: pageNo })
              }
            />
          </div>
        ) : (
          ""
        )}
        {isDateModalOpen && (
          <DateFilterModal
            openDateModal={isDateModalOpen}
            setOpenDateModal={() => setIsDateModalOpen(!isDateModalOpen)}
            handleChange={handleChange}
            startDate={startDate}
            endDate={endDate}
            onButtonClick={() => {
              setIsDateModalOpen(false);
              loadRoles();
            }}
          />
        )}
      </div>
    </div>
  );
};

export default SecondaryRoles;
